import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useAccountContextStore } from '@/stores/accountContext'
import { DEFAULT_ROWS_PER_PAGE } from '@/config/pagination'
import { getRowsPerPage } from '@/utils/rowsPerPage'
import type {
  Domain,
  DomainGroup,
  DomainVerification,
  DashboardSummary,
  DashboardHealth,
  DomainFilters,
  BulkImportResult,
  ChecklistItem,
  DomainStatusEvent,
} from '@/types'

const DEFAULT_FILTERS: DomainFilters = {
  search: '',
  status: '',
  blacklist_status: '',
  group_id: null,
  tag: '',
  email_dns_issues: false,
  sort_by: 'domain_name',
  sort_dir: 'asc',
  page: 1,
  per_page: DEFAULT_ROWS_PER_PAGE,
}

interface DomainListResponse {
  data: Domain[]
  total: number
  per_page: number
  current_page: number
}

interface AddDomainResponse {
  data: Domain
  verification?: DomainVerification
}

function mapBulkAction(action: string): string | null {
  switch (action) {
    case 'delete':
      return 'delete'
    case 'move':
      return 'move_to_group'
    case 'toggle_notifications':
      return 'disable_notifications'
    case 'tag':
      return 'add_tags'
    default:
      return action
  }
}

export const useDomainStore = defineStore('domains', () => {
  const authStore = useAuthStore()

  const domains = ref<Domain[]>([])
  const currentDomain = ref<Domain | null>(null)
  const groups = ref<DomainGroup[]>([])
  const summary = ref<DashboardSummary | null>(null)
  const health = ref<DashboardHealth | null>(null)
  const checklist = ref<ChecklistItem[]>([])
  const checklistDismissed = ref(false)
  const filters = ref<DomainFilters>({ ...DEFAULT_FILTERS })
  const totalDomains = ref(0)
  const totalPages = ref(0)
  const loading = ref(false)
  const loadingSummary = ref(false)
  const loadingHealth = ref(false)
  const summaryError = ref<string | null>(null)
  const healthError = ref<string | null>(null)

  const activeDomains = computed(() => domains.value.filter((d) => d.status === 'active'))
  const expiringDomains = computed(() => domains.value.filter((d) => d.status === 'expiring_soon'))

  function accountUuid(): string {
    const accountStore = useAccountContextStore()
    const uuid = accountStore.activeAccountUuid ?? authStore.user?.primary_account_uuid
    if (!uuid) {
      throw new Error('Active account not found')
    }
    return uuid
  }

  async function fetchDashboardSummary(): Promise<void> {
    loadingSummary.value = true
    summaryError.value = null
    try {
      const { data } = await apiClient.get<{ data: Record<string, number> }>('/dashboard/health')
      const counts = data.data
      summary.value = {
        total_domains: counts.total ?? 0,
        active: counts.active ?? 0,
        expiring_soon: counts.expiring_soon ?? 0,
        expired: counts.expired ?? 0,
        blacklisted: counts.blacklisted ?? 0,
        error: counts.error ?? 0,
        email_dns_issues: counts.email_dns_issues ?? 0,
        dns_zones: 0,
        ssl_issues: 0,
      }
    } catch {
      summaryError.value = 'Could not load dashboard summary.'
      summary.value = null
    } finally {
      loadingSummary.value = false
    }
  }

  async function fetchDashboardHealth(): Promise<void> {
    loadingHealth.value = true
    healthError.value = null
    try {
      const { data } = await apiClient.get<{
        data: {
          expiring_domains?: DashboardHealth['expiring_domains']
          blacklisted_domains?: DashboardHealth['blacklisted_domains']
          recent_dns_changes?: DashboardHealth['recent_dns_changes']
          recent_notifications?: DashboardHealth['recent_notifications']
          email_dns_issues?: DashboardHealth['email_dns_issues']
        }
      }>('/dashboard/summary')
      const payload = data.data
      health.value = {
        expiring_domains: payload.expiring_domains ?? [],
        blacklisted_domains: payload.blacklisted_domains ?? [],
        recent_dns_changes: payload.recent_dns_changes ?? [],
        recent_notifications: payload.recent_notifications ?? [],
        email_dns_issues: payload.email_dns_issues ?? [],
      }
    } catch {
      healthError.value = 'Could not load dashboard details.'
      health.value = null
    } finally {
      loadingHealth.value = false
    }
  }

  async function fetchDomains(): Promise<void> {
    loading.value = true
    try {
      const perPage = getRowsPerPage()
      filters.value.per_page = perPage

      const params: Record<string, string | number> = {
        page: filters.value.page,
        per_page: perPage,
        sort_by: filters.value.sort_by,
        sort_dir: filters.value.sort_dir,
      }
      if (filters.value.search) params.search = filters.value.search
      if (filters.value.status) params.status = filters.value.status
      if (filters.value.blacklist_status) params.blacklist_status = filters.value.blacklist_status
      if (filters.value.group_id) params.domain_group_id = filters.value.group_id
      if (filters.value.tag) params.tag = filters.value.tag
      if (filters.value.email_dns_issues) params.email_dns_issues = 1

      const { data } = await apiClient.get<DomainListResponse>('/domains', { params })
      domains.value = data.data
      totalDomains.value = data.total
      totalPages.value = Math.max(1, Math.ceil(data.total / data.per_page))
    } finally {
      loading.value = false
    }
  }

  async function fetchDomain(uuid: string): Promise<void> {
    loading.value = true
    try {
      const { data } = await apiClient.get<{ data: Domain }>(`/domains/${uuid}`)
      currentDomain.value = data.data
    } finally {
      loading.value = false
    }
  }

  async function refreshDomain(uuid: string): Promise<Domain> {
    const { data } = await apiClient.post<{ data: Domain }>(`/domains/${uuid}/refresh`)
    const updated = data.data

    if (currentDomain.value?.uuid === uuid) {
      currentDomain.value = updated
    }

    const index = domains.value.findIndex((domain) => domain.uuid === uuid)
    if (index !== -1) {
      domains.value[index] = updated
    }

    return updated
  }

  async function updateDomain(
    uuid: string,
    payload: Partial<
      Pick<
        Domain,
        | 'ssl_monitoring_enabled'
        | 'dns_monitoring_enabled'
        | 'notifications_enabled'
        | 'domain_group_id'
      >
    >,
  ): Promise<Domain> {
    const { data } = await apiClient.put<{ data: Domain }>(`/domains/${uuid}`, payload)
    const updated = data.data

    if (currentDomain.value?.uuid === uuid) {
      currentDomain.value = updated
    }

    const index = domains.value.findIndex((domain) => domain.uuid === uuid)
    if (index !== -1) {
      domains.value[index] = updated
    }

    return updated
  }

  async function addDomain(domainName: string, verificationMethod: string): Promise<Domain> {
    const { data } = await apiClient.post<AddDomainResponse>('/domains', {
      domain_name: domainName,
      verification_method: verificationMethod,
    })
    const domain = data.data
    if (data.verification) {
      domain.verifications = [data.verification]
    }
    return domain
  }

  async function deleteDomain(uuid: string): Promise<void> {
    await apiClient.delete(`/domains/${uuid}`)
    domains.value = domains.value.filter((d) => d.uuid !== uuid)
  }

  async function resendVerificationEmail(uuid: string): Promise<void> {
    await apiClient.post(`/domains/${uuid}/resend-verification-email`)
  }

  async function bulkImport(payload: { domains: string[]; file?: File }): Promise<BulkImportResult> {
    if (payload.file) {
      const formData = new FormData()
      formData.append('file', payload.file)
      const { data } = await apiClient.post<{ data: BulkImportResult }>('/domains/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data.data
    }
    const { data } = await apiClient.post<{ data: BulkImportResult }>('/domains/bulk-import', {
      domains: payload.domains,
    })
    return data.data
  }

  async function bulkOperation(
    action: string,
    domainIds: number[],
    params?: Record<string, unknown>,
  ): Promise<void> {
    const mappedAction = mapBulkAction(action)
    if (!mappedAction) {
      throw new Error(`Bulk action "${action}" is not supported yet`)
    }

    const body: Record<string, unknown> = {
      action: mappedAction,
      domain_ids: domainIds,
    }

    if (mappedAction === 'move_to_group' && params?.group_id !== undefined) {
      body.group_id = params.group_id
    }
    if (mappedAction === 'add_tags' && params?.tags) {
      body.tags = params.tags
    }

    await apiClient.post('/domains/bulk-operation', body)
    await fetchDomains()
  }

  async function fetchGroups(): Promise<void> {
    const { data } = await apiClient.get<{ data: DomainGroup[] }>(
      `/domain-groups/${accountUuid()}`
    )
    groups.value = data.data
  }

  async function createGroup(name: string, parentId?: number | null): Promise<DomainGroup> {
    const { data } = await apiClient.post<{ data: DomainGroup }>(
      `/domain-groups/${accountUuid()}`,
      { name, parent_id: parentId ?? null }
    )
    await fetchGroups()
    return data.data
  }

  async function renameGroup(id: number, name: string): Promise<void> {
    await apiClient.put(`/domain-groups/${accountUuid()}/${id}`, { name })
    await fetchGroups()
  }

  async function deleteGroup(id: number): Promise<void> {
    await apiClient.delete(`/domain-groups/${accountUuid()}/${id}`)
    await fetchGroups()
  }

  async function fetchChecklist(): Promise<void> {
    try {
      const { data } = await apiClient.get<{
        data: {
          dismissed: boolean
          steps: Array<{
            key: string
            title: string
            description: string
            completed: boolean
            link?: string
          }>
        }
      }>('/help/checklist')

      checklistDismissed.value = data.data.dismissed
      checklist.value = data.data.steps.map((step) => ({
        id: step.key,
        title: step.title,
        description: step.description,
        completed: step.completed,
        action_url: step.link?.startsWith('/app') ? step.link : step.link ? `/app${step.link}` : undefined,
      }))
    } catch {
      checklist.value = []
    }
  }

  async function dismissChecklist(): Promise<void> {
    checklistDismissed.value = true
    try {
      const { data } = await apiClient.post<{
        data: { dismissed: boolean }
      }>('/help/checklist', { dismiss: true })
      checklistDismissed.value = data.data.dismissed
    } catch {
      // local state is enough
    }
  }

  function setFilters(newFilters: Partial<DomainFilters>): void {
    filters.value = { ...filters.value, ...newFilters, page: 1 }
  }

  function setPage(page: number): void {
    filters.value.page = page
  }

  function resetFilters(): void {
    filters.value = { ...DEFAULT_FILTERS }
  }

  function domainMatchesEvent(domain: Domain, domainId: number | string): boolean {
    return domain.uuid === String(domainId) || domain.id === Number(domainId)
  }

  function applyStatusEvent(event: DomainStatusEvent): void {
    const idx = domains.value.findIndex((d) => domainMatchesEvent(d, event.domain_id))
    if (idx !== -1) {
      domains.value[idx] = {
        ...domains.value[idx],
        status: event.status,
        ...(event.blacklist_status && { blacklist_status: event.blacklist_status }),
        ...(event.ssl_status && { ssl_status: event.ssl_status }),
        ...(event.expiration_date && { expiration_date: event.expiration_date }),
        updated_at: event.updated_at,
      }
    }
    if (currentDomain.value && domainMatchesEvent(currentDomain.value, event.domain_id)) {
      currentDomain.value = {
        ...currentDomain.value,
        status: event.status,
        ...(event.blacklist_status && { blacklist_status: event.blacklist_status }),
        ...(event.ssl_status && { ssl_status: event.ssl_status }),
        ...(event.expiration_date && { expiration_date: event.expiration_date }),
        updated_at: event.updated_at,
      }
    }
  }

  return {
    domains,
    currentDomain,
    groups,
    summary,
    health,
    checklist,
    checklistDismissed,
    filters,
    totalDomains,
    totalPages,
    loading,
    loadingSummary,
    loadingHealth,
    summaryError,
    healthError,
    activeDomains,
    expiringDomains,
    fetchDashboardSummary,
    fetchDashboardHealth,
    fetchDomains,
    fetchDomain,
    refreshDomain,
    updateDomain,
    addDomain,
    deleteDomain,
    resendVerificationEmail,
    bulkImport,
    bulkOperation,
    fetchGroups,
    createGroup,
    renameGroup,
    deleteGroup,
    fetchChecklist,
    dismissChecklist,
    setFilters,
    setPage,
    resetFilters,
    applyStatusEvent,
  }
})
